import { useCallback, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type {
	AddWorkflowCanvasEdgeInput,
	AddWorkflowCanvasElementInput,
	WorkflowCanvasActionData,
	WorkflowCanvasControlData,
	WorkflowCanvasEdge,
	WorkflowCanvasElement,
	WorkflowCanvasPosition,
} from "./workflow-canvas.types";

interface TreeValidationResult {
	isExecutable: boolean;
	hasCycle: boolean;
	hasSingleRoot: boolean;
	hasValidInDegree: boolean;
	isConnected: boolean;
}

const DEFAULT_ACTION_DATA: WorkflowCanvasActionData = {
	moduleType: "generic",
	name: "Action",
	description: "New action",
	parameterName: "action-1",
	gyulAmount: 1,
};

const DEFAULT_CONTROL_DATA: WorkflowCanvasControlData = {
	moduleType: "generic",
	name: "Control",
	description: "New control",
	parameterName: "control-1",
};

const DEFAULT_START_POSITION: WorkflowCanvasPosition = {
	x: 48,
	y: 48,
};

const DEFAULT_GAP_X = 300;
const DEFAULT_GAP_Y = 180;
const DEFAULT_COLUMNS = 3;

const createElementId = () => {
	if (typeof globalThis.crypto?.randomUUID === "function") {
		return globalThis.crypto.randomUUID();
	}

	return `wf-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toModuleKey = (value: string) => {
	return (
		value
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "") || "node"
	);
};

const getNextParameterName = (
	elements: WorkflowCanvasElement[],
	moduleType: string,
	fallback: string,
) => {
	const prefix = toModuleKey(moduleType || fallback);
	const used = new Set<string>();

	for (const element of elements) {
		used.add(element.data.parameterName);
	}

	let index = 1;
	while (used.has(`${prefix}-${index}`)) {
		index += 1;
	}

	return `${prefix}-${index}`;
};

const buildAdjacency = (edges: WorkflowCanvasEdge[]) => {
	const adjacency = new Map<string, string[]>();

	for (const edge of edges) {
		const current = adjacency.get(edge.fromElementId);
		if (current) {
			current.push(edge.toElementId);
		} else {
			adjacency.set(edge.fromElementId, [edge.toElementId]);
		}
	}

	return adjacency;
};

const hasPath = (from: string, target: string, adjacency: Map<string, string[]>) => {
	const stack = [from];
	const visited = new Set<string>();

	while (stack.length > 0) {
		const current = stack.pop();
		if (!current) {
			continue;
		}

		if (current === target) {
			return true;
		}

		if (visited.has(current)) {
			continue;
		}

		visited.add(current);

		const nextNodes = adjacency.get(current) ?? [];
		for (const nextNode of nextNodes) {
			if (!visited.has(nextNode)) {
				stack.push(nextNode);
			}
		}
	}

	return false;
};

const createsCycle = (fromElementId: string, toElementId: string, edges: WorkflowCanvasEdge[]) => {
	const adjacency = buildAdjacency(edges);
	return hasPath(toElementId, fromElementId, adjacency);
};

const validateTreeExecution = (
	elements: WorkflowCanvasElement[],
	edges: WorkflowCanvasEdge[],
): TreeValidationResult => {
	if (elements.length === 0) {
		return {
			isExecutable: false,
			hasCycle: false,
			hasSingleRoot: false,
			hasValidInDegree: false,
			isConnected: false,
		};
	}

	const elementIds = new Set(elements.map((element) => element.id));
	const inDegree = new Map<string, number>();
	for (const element of elements) {
		inDegree.set(element.id, 0);
	}

	for (const edge of edges) {
		if (!elementIds.has(edge.fromElementId) || !elementIds.has(edge.toElementId)) {
			return {
				isExecutable: false,
				hasCycle: true,
				hasSingleRoot: false,
				hasValidInDegree: false,
				isConnected: false,
			};
		}

		inDegree.set(edge.toElementId, (inDegree.get(edge.toElementId) ?? 0) + 1);
	}

	const roots = elements.filter((element) => (inDegree.get(element.id) ?? 0) === 0);
	const hasSingleRoot = roots.length === 1;
	const hasValidInDegree = elements.every((element) => {
		const degree = inDegree.get(element.id) ?? 0;
		if (degree === 0) {
			return true;
		}

		return degree === 1;
	});

	const adjacency = buildAdjacency(edges);
	const color = new Map<string, 0 | 1 | 2>();
	let hasCycle = false;

	const visit = (nodeId: string) => {
		if (hasCycle) {
			return;
		}

		const state = color.get(nodeId) ?? 0;
		if (state === 1) {
			hasCycle = true;
			return;
		}

		if (state === 2) {
			return;
		}

		color.set(nodeId, 1);

		const nextNodes = adjacency.get(nodeId) ?? [];
		for (const nextNode of nextNodes) {
			visit(nextNode);
		}

		color.set(nodeId, 2);
	};

	for (const element of elements) {
		visit(element.id);
	}

	let isConnected = false;
	if (hasSingleRoot) {
		const rootId = roots[0]?.id;
		if (rootId) {
			const visited = new Set<string>();
			const queue = [rootId];

			while (queue.length > 0) {
				const current = queue.shift();
				if (!current || visited.has(current)) {
					continue;
				}

				visited.add(current);

				const nextNodes = adjacency.get(current) ?? [];
				for (const nextNode of nextNodes) {
					if (!visited.has(nextNode)) {
						queue.push(nextNode);
					}
				}
			}

			isConnected = visited.size === elements.length;
		}
	}

	const isExecutable = hasSingleRoot && hasValidInDegree && !hasCycle && isConnected;

	return {
		isExecutable,
		hasCycle,
		hasSingleRoot,
		hasValidInDegree,
		isConnected,
	};
};

const getAutoPosition = (index: number): WorkflowCanvasPosition => {
	const column = index % DEFAULT_COLUMNS;
	const row = Math.floor(index / DEFAULT_COLUMNS);

	return {
		x: DEFAULT_START_POSITION.x + column * DEFAULT_GAP_X,
		y: DEFAULT_START_POSITION.y + row * DEFAULT_GAP_Y,
	};
};

const resolvePosition = (
	inputPosition: AddWorkflowCanvasElementInput["position"] | undefined,
	elementsCount: number,
) => {
	const autoPosition = getAutoPosition(elementsCount);

	return {
		x: inputPosition?.x ?? autoPosition.x,
		y: inputPosition?.y ?? autoPosition.y,
	};
};

export const getDefaultWorkflowCanvasElements = (): WorkflowCanvasElement[] => {
	return [];
};

export interface UseWorkflowCanvasElementsOptions {
	initialElements?: WorkflowCanvasElement[];
}

export interface WorkflowCanvasElementsController {
	elements: WorkflowCanvasElement[];
	edges: WorkflowCanvasEdge[];
	isExecutable: boolean;
	treeValidation: TreeValidationResult;
	selectedElementId: string | null;
	addElement: (input: AddWorkflowCanvasElementInput) => string;
	addEdge: (input: AddWorkflowCanvasEdgeInput) => string | null;
	updateElement: (
		id: string,
		updater: (current: WorkflowCanvasElement) => WorkflowCanvasElement,
	) => void;
	moveElement: (id: string, position: WorkflowCanvasPosition) => void;
	removeElement: (id: string) => void;
	removeEdge: (id: string) => void;
	selectElement: (id: string | null) => void;
	clearSelection: () => void;
	setElements: Dispatch<SetStateAction<WorkflowCanvasElement[]>>;
	setEdges: Dispatch<SetStateAction<WorkflowCanvasEdge[]>>;
}

export const useWorkflowCanvasElements = (
	options?: UseWorkflowCanvasElementsOptions,
): WorkflowCanvasElementsController => {
	const [elements, setElements] = useState<WorkflowCanvasElement[]>(
		() => options?.initialElements ?? [],
	);
	const [edges, setEdges] = useState<WorkflowCanvasEdge[]>([]);
	const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
	const treeValidation = useMemo(() => validateTreeExecution(elements, edges), [elements, edges]);

	const addElement = useCallback((input: AddWorkflowCanvasElementInput) => {
		const id = createElementId();

		setElements((current) => {
			const position = resolvePosition(input.position, current.length);

			if (input.kind === "action") {
				const moduleType = input.data?.moduleType ?? DEFAULT_ACTION_DATA.moduleType;
				const parameterName =
					input.data?.parameterName ?? getNextParameterName(current, moduleType, "action");

				return [
					...current,
					{
						id,
						kind: "action",
						position,
						data: {
							...DEFAULT_ACTION_DATA,
							...input.data,
							moduleType,
							parameterName,
						},
					},
				];
			}

			const moduleType = input.data?.moduleType ?? DEFAULT_CONTROL_DATA.moduleType;
			const parameterName =
				input.data?.parameterName ?? getNextParameterName(current, moduleType, "control");

			return [
				...current,
				{
					id,
					kind: "control",
					position,
					data: {
						...DEFAULT_CONTROL_DATA,
						...input.data,
						moduleType,
						parameterName,
					},
				},
			];
		});

		return id;
	}, []);

	const updateElement = useCallback(
		(id: string, updater: (current: WorkflowCanvasElement) => WorkflowCanvasElement) => {
			setElements((current) =>
				current.map((element) => {
					if (element.id !== id) {
						return element;
					}

					const updated = updater(element);
					return {
						...updated,
						id: element.id,
						position: updated.position,
					};
				}),
			);
		},
		[],
	);

	const addEdge = useCallback(
		(input: AddWorkflowCanvasEdgeInput) => {
			if (input.fromElementId === input.toElementId) {
				return null;
			}

			const elementIds = new Set(elements.map((element) => element.id));
			if (!elementIds.has(input.fromElementId) || !elementIds.has(input.toElementId)) {
				return null;
			}

			const id = createElementId();
			let createdId: string | null = null;

			setEdges((current) => {
				const alreadyExists = current.some(
					(edge) =>
						edge.fromElementId === input.fromElementId &&
						edge.toElementId === input.toElementId &&
						edge.sourcePort === input.sourcePort,
				);

				if (alreadyExists) {
					return current;
				}

				if (createsCycle(input.fromElementId, input.toElementId, current)) {
					return current;
				}

				createdId = id;

				return [
					...current,
					{
						id,
						fromElementId: input.fromElementId,
						toElementId: input.toElementId,
						label: input.label,
						sourcePort: input.sourcePort,
					},
				];
			});

			return createdId;
		},
		[elements],
	);

	const moveElement = useCallback((id: string, position: WorkflowCanvasPosition) => {
		setElements((current) =>
			current.map((element) => {
				if (element.id !== id) {
					return element;
				}

				return {
					...element,
					position,
				};
			}),
		);
	}, []);

	const removeElement = useCallback((id: string) => {
		setElements((current) => current.filter((element) => element.id !== id));
		setEdges((current) =>
			current.filter((edge) => edge.fromElementId !== id && edge.toElementId !== id),
		);
		setSelectedElementId((current) => (current === id ? null : current));
	}, []);

	const removeEdge = useCallback((id: string) => {
		setEdges((current) => current.filter((edge) => edge.id !== id));
	}, []);

	const selectElement = useCallback((_id: string | null) => {
		// TODO: temporarily disabled node selection
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedElementId(null);
	}, []);

	return {
		elements,
		edges,
		isExecutable: treeValidation.isExecutable,
		treeValidation,
		selectedElementId,
		addElement,
		addEdge,
		updateElement,
		moveElement,
		removeElement,
		removeEdge,
		selectElement,
		clearSelection,
		setElements,
		setEdges,
	};
};
